import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
 ArrowLeft,
 Send,
 User,
 Shield,
 Calendar,
 AlertCircle,
 MessageSquare,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { SearchSelect, Button } from '../../components/shared';
import {
 getAdminSupportTicket,
 updateAdminSupportTicket,
 replyToSupportTicket,
} from '../../services/supportTicketService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const STATUS_OPTIONS = [
 { value: 'open', label: 'Open' },
 { value: 'in_progress', label: 'In Progress' },
 { value: 'resolved', label: 'Resolved' },
 { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
 { value: 'low', label: 'Low' },
 { value: 'medium', label: 'Medium' },
 { value: 'high', label: 'High' },
 { value: 'urgent', label: 'Urgent' },
];

const getStatusColor = (status) => {
 const config = {
 open: 'bg-surface-muted text-ink',
 in_progress: 'bg-amber-50 text-amber-800',
 resolved: 'bg-emerald-50 text-emerald-800',
 closed: 'bg-gray-100 text-gray-800',
 };
 return config[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityColor = (priority) => {
 const config = {
 low: 'bg-emerald-50 text-emerald-800',
 medium: 'bg-amber-50 text-amber-800',
 high: 'bg-orange-50 text-orange-800',
 urgent: 'bg-red-50 text-red-700',
 };
 return config[priority] || 'bg-gray-100 text-gray-800';
};

const AdminSupportTicketDetail = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const queryClient = useQueryClient();
 const { user } = useAuth();
 const { success, error: showError } = useToastContext();
 const [replyText, setReplyText] = useState('');
 const [isInternal, setIsInternal] = useState(false);

 const { data: ticketResponse, isLoading, error } = useQuery({
 queryKey: ['adminSupportTicket', id],
 queryFn: () => getAdminSupportTicket(id),
 enabled: !!id,
 });

 const updateMutation = useMutation({
 mutationFn: ({ id, data }) => updateAdminSupportTicket(id, data),
 onSuccess: () => {
 queryClient.invalidateQueries(['adminSupportTicket', id]);
 queryClient.invalidateQueries(['adminSupportTickets']);
 success('Ticket updated successfully');
 },
 onError: (err) => {
 showError(err?.response?.data?.message || 'Failed to update ticket');
 },
 });

 const replyMutation = useMutation({
 mutationFn: (data) => replyToSupportTicket(id, data),
 onSuccess: () => {
 setReplyText('');
 setIsInternal(false);
 queryClient.invalidateQueries(['adminSupportTicket', id]);
 queryClient.invalidateQueries(['adminSupportTickets']);
 success('Reply sent successfully');
 },
 onError: (err) => {
 showError(err?.response?.data?.message || 'Failed to send reply');
 },
 });

 const ticket = ticketResponse?.data;

 const handleReply = (e) => {
 e.preventDefault();
 if (!replyText.trim()) return;
 replyMutation.mutate({ response: replyText.trim(), is_internal: isInternal });
 };

 const handleStatusChange = (value) => {
 updateMutation.mutate({ id, data: { status: value } });
 };

 const handlePriorityChange = (value) => {
 updateMutation.mutate({ id, data: { priority: value } });
 };

 if (isLoading) {
 return (
 <div className="space-y-5">
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading ticket…</p>
 </div>
 </div>
 );
 }

 if (error || !ticket) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Ticket Not Found</h3>
 <p className="text-red-600 text-sm mb-4">
 {error?.response?.data?.message || 'This support ticket could not be found.'}
 </p>
 <Button className="rounded-md whitespace-nowrap" onClick={() => navigate('/admin/support-tickets')}>
 <ArrowLeft className="w-4 h-4 mr-2 shrink-0" />
 Back to Support Tickets
 </Button>
 </div>
 </div>
 );
 }

 const replies = ticket.ticket_replies || ticket.ticketReplies || [];
 const sortedReplies = [...replies].sort(
 (a, b) => new Date(a.created_at) - new Date(b.created_at)
 );

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title={`Ticket #${String(ticket.id).slice(0, 8)}`}
 subtitle={ticket.subject}
 actions={
 <Button
 variant="outline"
 className="rounded-md whitespace-nowrap shrink-0"
 onClick={() => navigate('/admin/support-tickets')}
 >
 <ArrowLeft className="w-4 h-4 mr-2 shrink-0" />
 Back to tickets
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.supportTicketDetail.title}
 steps={ADMIN_PAGE_GUIDES.supportTicketDetail.steps}
 icon={ListChecks}
 />

 <div className="product-panel p-4 sm:p-6">
 <div className="flex flex-wrap gap-2 mb-4">
 <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
 {ticket.status?.replace('_', ' ')}
 </span>
 <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
 {ticket.priority}
 </span>
 <span className="text-sm text-ink-tertiary capitalize">{ticket.category}</span>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
 <div>
 <label className="text-xs font-medium text-ink-tertiary uppercase">Requester</label>
 <div className="flex items-center gap-2 mt-1">
 <User className="w-4 h-4 text-gray-400" />
 <div>
 <p className="font-medium text-ink">{ticket.user?.name || 'N/A'}</p>
 <p className="text-sm text-ink-tertiary">{ticket.user?.email}</p>
 </div>
 </div>
 </div>
 <div>
 <label className="text-xs font-medium text-ink-tertiary uppercase">Assigned To</label>
 <div className="flex items-center gap-2 mt-1">
 <Shield className="w-4 h-4 text-gray-400" />
 <p className="text-ink">{ticket.assignedTo?.name || 'Unassigned'}</p>
 </div>
 </div>
 <div>
 <label className="text-xs font-medium text-ink-tertiary uppercase">Created</label>
 <div className="flex items-center gap-2 mt-1">
 <Calendar className="w-4 h-4 text-gray-400" />
 <span className="text-ink">{formatDate(ticket.created_at, 'medium')} {formatTime(ticket.created_at)}</span>
 </div>
 </div>
 </div>

 <div className="flex flex-wrap gap-3 mb-6 p-3 rounded-xl bg-surface-muted border border-border">
 <div className="w-40">
 <label className="text-xs font-medium text-ink-tertiary block mb-1">Status</label>
 <SearchSelect
 value={ticket.status}
 onChange={handleStatusChange}
 options={STATUS_OPTIONS}
 size="sm"
 />
 </div>
 <div className="w-40">
 <label className="text-xs font-medium text-ink-tertiary block mb-1">Priority</label>
 <SearchSelect
 value={ticket.priority}
 onChange={handlePriorityChange}
 options={PRIORITY_OPTIONS}
 size="sm"
 />
 </div>
 {!ticket.assigned_to && user?.id && (
 <div className="flex items-end">
 <Button
 className="rounded-md"
 onClick={() => updateMutation.mutate({ id, data: { assigned_to: user.id } })}
 disabled={updateMutation.isPending}
 >
 Assign to me
 </Button>
 </div>
 )}
 </div>

 <div>
 <label className="text-xs font-medium text-ink-tertiary uppercase">Description</label>
 <p className="mt-2 text-ink-secondary whitespace-pre-wrap">{ticket.description}</p>
 </div>

 {ticket.resolution_notes && (
 <div className="mt-4 pt-4 border-t border-border">
 <label className="text-xs font-medium text-ink-tertiary uppercase">Resolution Notes</label>
 <p className="mt-2 text-ink-secondary">{ticket.resolution_notes}</p>
 </div>
 )}
 </div>

 <div className="product-panel p-4 sm:p-6">
 <h3 className="font-semibold text-ink mb-4 flex items-center gap-2">
 <MessageSquare className="w-5 h-5" />
 Conversation ({sortedReplies.length} {sortedReplies.length === 1 ? 'reply' : 'replies'})
 </h3>

 <div className="space-y-4 mb-6">
 {sortedReplies.map((reply) => (
 <div
 key={reply.id}
 className={`p-4 rounded-xl ${ reply.response_type === 'admin' ? 'bg-surface-muted border-l-4 border-ink' : 'bg-surface-muted border-l-4 border-gray-300' }`}
 >
 <div className="flex items-center justify-between mb-2">
 <div className="flex items-center gap-2">
 {reply.response_type === 'admin' ? (
 <Shield className="w-4 h-4 text-ink" />
 ) : (
 <User className="w-4 h-4 text-ink-secondary" />
 )}
 <span className="font-medium text-ink">
 {reply.responder?.name || 'Support'}
 </span>
 {reply.is_internal && (
 <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-800 rounded">
 Internal
 </span>
 )}
 </div>
 <span className="text-xs text-ink-tertiary">
 {formatDate(reply.created_at, 'medium')} {formatTime(reply.created_at)}
 </span>
 </div>
 <p className="text-ink-secondary whitespace-pre-wrap">
 {reply.response_text || reply.response}
 </p>
 </div>
 ))}
 </div>

 <form onSubmit={handleReply} className="space-y-3">
 <textarea
 value={replyText}
 onChange={(e) => setReplyText(e.target.value)}
 placeholder="Type your reply..."
 rows={4}
 className="w-full px-3 py-2 border border-border rounded-xl bg-surface text-ink focus:ring-2 focus:ring-ink focus:border-ink"
 disabled={replyMutation.isPending}
 />
 <div className="flex items-center justify-between">
 <label className="flex items-center gap-2 text-sm text-ink-secondary">
 <input
 type="checkbox"
 checked={isInternal}
 onChange={(e) => setIsInternal(e.target.checked)}
 className="rounded border-gray-300"
 />
 Internal note (not visible to user)
 </label>
 <Button
 type="submit"
 className="rounded-md inline-flex items-center gap-2"
 disabled={!replyText.trim() || replyMutation.isPending}
 >
 <Send className="w-4 h-4" />
 {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
 </Button>
 </div>
 </form>
 </div>
 </div>
 );
};

export default AdminSupportTicketDetail;
